import React from 'react';
import { Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClientBookingItem } from '../../../types/client';
import { ClientStatusBadge, STATUS_PALETTE } from './ClientStatusBadge';
import { PaymentStatusBadge } from '../../shared/PaymentStatusBadge';

interface Props {
  booking: ClientBookingItem;
  onRebook?: (booking: ClientBookingItem) => void;
  onCancelRequest?: (id: number, uuid: string) => void;
}

export const BookingCard: React.FC<Props> = ({ booking, onRebook, onCancelRequest }) => {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const cfg = STATUS_PALETTE[booking.status] ?? STATUS_PALETTE['PENDING'];

  const canCancel =
    onCancelRequest &&
    booking.canRequestCancel &&
    !booking.cancellationDeadlinePassed;

  const deadlinePassed =
    onCancelRequest &&
    booking.canRequestCancel &&
    booking.cancellationDeadlinePassed;

  return (
    <div
      style={{
        margin: '0 16px 10px',
        borderRadius: 'var(--bl-radius-lg)',
        background: 'var(--bl-surface)',
        boxShadow: 'var(--bl-shadow)',
        border: '1px solid var(--bl-divider)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        cursor: 'pointer',
      }}
      onClick={() => navigate(`/bookings/${booking.bookingUuid}`)}
    >
      {/* Left accent bar */}
      <div style={{ width: 4, background: cfg.color, flexShrink: 0 }} />

      {/* Content */}
      <div style={{ flex: 1, padding: '13px 14px' }}>
        {/* Service + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text)', flex: 1, minWidth: 0 }}>
            {booking.serviceName}
          </Typography.Text>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
            <ClientStatusBadge status={booking.status} />
            {booking.price > 0 && (
              <PaymentStatusBadge status={booking.paymentStatus} size="sm" />
            )}
          </div>
        </div>

        {/* Provider */}
        <Typography.Text style={{ display: 'block', fontSize: 13, color: 'var(--bl-text-secondary)', marginTop: 3 }}>
          {booking.providerName}
        </Typography.Text>

        {/* Time block */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: cfg.bg,
          borderRadius: 'var(--bl-radius-sm)', padding: '5px 10px', marginTop: 8,
        }}>
          <ClockCircleOutlined style={{ fontSize: 12, color: cfg.color }} />
          <Typography.Text style={{ fontSize: 13, fontWeight: 600, color: cfg.color }}>
            {booking.time} · {t('bookings.card.durationShort', { count: booking.durationMinutes })}
          </Typography.Text>
        </div>

        {/* Price */}
        {booking.price > 0 && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginTop: 8 }}>
            <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text)', letterSpacing: -0.3 }}>
              {booking.price.toLocaleString()}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 13, color: 'var(--bl-text-secondary)' }}>
              {t('common.currency')}
            </Typography.Text>
          </div>
        )}

        {/* Action buttons */}
        {(canCancel || deadlinePassed || onRebook) && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
            {onRebook && (
              <button
                onClick={() => onRebook(booking)}
                style={{
                  flex: 1, height: 44, borderRadius: 'var(--bl-radius-sm)',
                  background: 'var(--bl-surface)',
                  color: 'var(--bl-primary)',
                  border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                {t('bookings.card.rebook')}
              </button>
            )}
            {canCancel && onCancelRequest && (
              <button
                onClick={() => onCancelRequest(booking.id, booking.bookingUuid)}
                style={{
                  flex: 1, height: 44, borderRadius: 'var(--bl-radius-sm)',
                  background: STATUS_PALETTE.CANCELLED.bg,
                  color: STATUS_PALETTE.CANCELLED.color,
                  border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                {t('bookings.card.cancel')}
              </button>
            )}
            {deadlinePassed && (
              <button
                disabled
                style={{
                  flex: 1, height: 44, borderRadius: 'var(--bl-radius-sm)',
                  background: 'var(--bl-surface)',
                  color: 'var(--bl-text-secondary)',
                  border: 'none', cursor: 'not-allowed',
                  fontSize: 13, fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                {t('bookings.card.deadlinePassed')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
